import React, { useState, useMemo } from 'react';
import { Search, Filter, Download, Phone, Zap } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { STATUS_FILTER_OPTIONS, CHANNEL_TR, statusLabel } from '../i18n/tr';
import { filterCalls } from '../utils/callFilters';
import { resolveDisplayStatus } from '../utils/callStatus';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import StatusBadge from './ui/StatusBadge';
import AppointmentDetailModal from './ui/AppointmentDetailModal';
import { maskPhoneNumber } from '../utils/auth';

const CallLogs = ({ callLogs = [], isAdmin = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('today');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCall, setSelectedCall] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const itemsPerPage = 20;

  const dateOptions = [
    { value: 'today', label: 'Bugün' },
    { value: 'week', label: 'Bu hafta' },
    { value: 'month', label: 'Bu ay' },
    { value: 'all', label: 'Tümü' },
  ];

  const filteredCalls = useMemo(
    () => filterCalls(callLogs, { searchTerm, statusFilter, dateFilter }),
    [callLogs, searchTerm, statusFilter, dateFilter]
  );

  const openSummaryModal = (call) => {
    setSelectedCall(call);
    setShowModal(true);
  };

  const closeSummaryModal = () => {
    setShowModal(false);
    setSelectedCall(null);
  };

  const totalPages = Math.ceil(filteredCalls.length / itemsPerPage);
  const paginatedCalls = filteredCalls.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const displayPhone = (call) => {
    let phone;
    if (call.channel && CHANNEL_TR[call.channel]) {
      phone = call.appointment?.customerPhone || CHANNEL_TR[call.channel];
    } else {
      phone = call.phoneNumber || '—';
    }
    return isAdmin ? phone : maskPhoneNumber(phone);
  };

  const exportToCSV = () => {
    const headers = [
      'Telefon',
      'Çağrı zamanı',
      'Süre',
      'Durum',
      'Plaka',
      'KM',
      'İşlem',
      ...(isAdmin ? ['Müşteri', 'Özet'] : []),
    ];
    const csvContent = [
      headers.join(','),
      ...filteredCalls.map((call) => {
        const row = [
          displayPhone(call),
          format(new Date(call.callTime), 'yyyy-MM-dd HH:mm:ss'),
          call.duration,
          statusLabel(resolveDisplayStatus(call)),
          call.appointment?.plate || '—',
          call.appointment?.kilometer ?? '—',
          call.serviceType || '—',
        ];
        if (isAdmin) {
          row.push(
            call.appointment
              ? `${call.appointment.firstName || ''} ${call.appointment.lastName || ''}`.trim()
              : '—',
            `"${(call.summary || '').replace(/"/g, '""')}"`
          );
        }
        return row.join(',');
      }),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `cagri-kayitlari-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const liveBadge = (
    <div className="flex items-center rounded-lg bg-emerald-400/10 px-3 py-2 ring-1 ring-emerald-400/20">
      <div className="mr-2 h-2 w-2 animate-pulse rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
      <span className="text-sm font-medium text-emerald-300">Canlı güncelleme</span>
    </div>
  );

  return (
    <div className="page-container">
      <PageHeader
        title="Çağrı Kayıtları"
        description="Telefon ve web paneli görüşmelerinin anlık kaydı"
        badge={liveBadge}
      />

      <div className="card mb-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder={isAdmin ? 'Telefon, plaka, özet veya işlem ara…' : 'Plaka veya işlem ara…'}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="field w-full py-2 pl-10 pr-4"
            />
          </div>

          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="field"
              >
                {STATUS_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <select
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="field"
            >
              {dateOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={exportToCSV}
              disabled={filteredCalls.length === 0}
              className="btn-primary flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              CSV indir
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-slate-400">
            {paginatedCalls.length} / {filteredCalls.length} çağrı
          </div>
          <div className="flex items-center text-sm text-emerald-300">
            <Zap className="mr-1 h-3 w-3" />
            Canlı veri
          </div>
        </div>
      </div>

      <div className="card">
        {callLogs.length === 0 ? (
          <EmptyState
            icon={Phone}
            title="Henüz çağrı kaydı yok"
            description="Sağ alttaki asistanla konuştuğunuzda veya telefon hattından gelen çağrılar burada listelenir. Geliştirme modunda sol menüden örnek çağrı ekleyebilirsiniz."
          />
        ) : filteredCalls.length === 0 ? (
          <EmptyState
            icon={Search}
            title="Eşleşen çağrı bulunamadı"
            description="Arama veya tarih filtresini değiştirmeyi deneyin."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                      Zaman
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                      Kaynak / Telefon
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                      Süre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                      Durum
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                      Plaka
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                      KM
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                      İşlem
                    </th>
                    {isAdmin && (
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                        Özet
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                      Detay
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {paginatedCalls.map((call, index) => (
                    <tr
                      key={call.id}
                      className={`transition-colors hover:bg-white/5 ${index === 0 && currentPage === 1 ? 'bg-emerald-400/[0.07]' : ''}`}
                    >
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-200">
                        {format(new Date(call.callTime), 'd MMM HH:mm:ss', { locale: tr })}
                        {index === 0 && currentPage === 1 && (
                          <span className="ml-2 inline-flex items-center rounded bg-emerald-400/15 px-2 py-0.5 text-xs font-medium text-emerald-300 ring-1 ring-emerald-400/20">
                            En yeni
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-white">
                        {displayPhone(call)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-300">{call.duration}</td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <StatusBadge call={call} />
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-slate-200">
                        {call.appointment?.plate || '—'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-300">
                        {call.appointment?.kilometer != null
                          ? call.appointment.kilometer.toLocaleString('tr-TR')
                          : '—'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-300">
                        {call.appointment?.serviceType || call.serviceType || '—'}
                      </td>
                      {isAdmin && (
                        <td className="max-w-md px-6 py-4 text-sm text-slate-300">
                          <div className="truncate" title={call.summary}>
                            {call.summary}
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm">
                        <button
                          type="button"
                          onClick={() => openSummaryModal(call)}
                          className="text-accent-300 hover:text-accent-200"
                        >
                          Detay
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-white/10 px-6 py-3">
                <div className="text-sm text-slate-300">
                  Sayfa {currentPage} / {totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="btn-secondary disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Önceki
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="btn-secondary disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Sonraki
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showModal && selectedCall && (
        <AppointmentDetailModal
          call={selectedCall}
          title="Görüşme detayı"
          onClose={closeSummaryModal}
          privacyMode={!isAdmin}
        />
      )}
    </div>
  );
};

export default CallLogs;
